'use client'

import React, { useState } from 'react'
import { validatePhoneNumber } from '@/utils/phoneFormatter'
import { useModal } from '@/blocks/redesign_blocks/Modal/ModalContext'
import { useForm } from 'react-hook-form'
import { useHookFormMask } from 'use-mask-input'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import validator from 'validator'
import submitForm from '@/utils/submitFormMail'
import styles from './styles.module.css'

const formSchema = z.object({
  name: z.string().optional(),
  phone: z
    .string()
    .min(1, {
      message: 'Необходимо заполнить поле'
    })
    .refine(
      val =>
        validator.isMobilePhone(
          val.slice(1).replaceAll('(', '').replaceAll(')', '').replaceAll(' ', '').replaceAll('-', ''),
          'ru-RU'
        ),
      {
        message: 'Поле Введено некоректно'
      }
    ),
  description: z.string().optional(),
  privacy: z.boolean().refine(val => val === true, {
    message: 'Необходимо согласиться'
  })
})

const Contact: React.FC = () => {
  const { openSuccessModal } = useModal()
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      phone: '',
      description: '',
      privacy: false
    }
  })
  
  const registerWithMask = useHookFormMask(form.register)

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true)
    
    try {
      // Создаем FormData для отправки на сервер
      const formData = new FormData()
      formData.set('name', values.name || '')
      formData.set('phone', values.phone)
      formData.set('description', values.description || '')
      
      // Отправляем данные на бэкэнд
      await submitForm(formData)
      
      // Если отправка успешна, показываем модалку
      form.reset()
      openSuccessModal()
    } catch (error) {
      console.error('Ошибка отправки формы:', error)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className={styles.contact}>
      <div className={styles.contactContainer}>
        <div className={styles.contactContent}>
          {/* Text block */}
          <div className={styles.textBlock}>
            <h2 className={styles.title}>
              Ваша цифровая трансформация начинается здесь
            </h2>
            <p 
              className={styles.description}
              dangerouslySetInnerHTML={{ 
                __html: 'Оставьте заявку на бесплатную консультацию — наши эксперты помогут выявить ваши потребности и подобрать оптимальное решение' 
              }}
            />
          </div>

          {/* Form */}
          <form className={styles.form} onSubmit={form.handleSubmit(onSubmit)} noValidate>
            {/* Name input */}
            <div className={styles.inputGroup}>
              <div className={styles.inputContainer}>
                <input 
                  {...form.register('name')}
                  type="text" 
                  placeholder="Ваше имя"
                  className={styles.input}
                />
              </div>
            </div>

            {/* Phone input */}
            <div className={styles.inputGroup}>
              <div className={styles.inputContainer}>
                <input 
                  {...registerWithMask('phone', ['+7 (999) 999-99-99'])}
                  type="tel" 
                  placeholder="Телефон *"
                  className={styles.input}
                />
              </div>
              {form.formState.errors.phone && (
                <div className={styles.errorMessage}>{form.formState.errors.phone.message}</div>
              )}
            </div>

            {/* Description textarea */}
            <div className={styles.textareaGroup}>
              <div className={styles.textareaContainer}>
                <textarea 
                  {...form.register('description')}
                  placeholder="Опишите задачу или проект"
                  className={styles.textarea}
                  rows={4}
                />
              </div>
            </div>

            {/* Privacy policy checkbox */}
            <div className={`${styles.checkboxGroup} ${form.formState.errors.privacy ? styles.checkboxGroupError : ''}`}>
              <div className={styles.checkboxContainer}>
                <div className={styles.checkboxWrapper}>
                  <input 
                    {...form.register('privacy')}
                    type="checkbox" 
                    id="privacy"
                    className={styles.checkboxInput}
                  />
                  <label htmlFor="privacy" className={styles.checkboxLabel}>
                    {/* Unchecked state */}
                    <svg 
                      className={styles.checkboxUnchecked}
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M19 5V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z" fill="#C9C9C9"/>
                    </svg>
                    {/* Checked state */}
                    <svg 
                      className={styles.checkboxChecked}
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM10.71 16.29C10.32 16.68 9.69 16.68 9.3 16.29L5.71 12.7C5.32 12.31 5.32 11.68 5.71 11.29C6.1 10.9 6.73 10.9 7.12 11.29L10 14.17L16.88 7.29C17.27 6.9 17.9 6.9 18.29 7.29C18.68 7.68 18.68 8.31 18.29 8.7L10.71 16.29Z" fill="#C9C9C9"/>
                    </svg>
                  </label>
                </div>
              </div>
              <label htmlFor="privacy" className={styles.checkboxText}>
                Я ознакомился с <a href="/privacy.pdf" target="_blank" rel="noopener noreferrer" className={styles.privacyLink}>политикой конфиденциальности</a> и согласен на обработку персональных данных
              </label>
            </div>

            {/* Submit button */}
            <div className={styles.buttonGroup}>
              <button 
                type="submit" 
                className={styles.submitButton}
                disabled={isSubmitting}
              >
                <span className={styles.buttonText}>
                  {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>

    </section>
  )
}

Contact.displayName = 'Contact'

export default Contact
